import { FormikErrors } from 'formik';
import * as Yup from 'yup';
import type { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import { formatInternationalPhoneNumberE164 } from '@rsos/utils/internationalPhoneNumberUtils';
import { type Checkbox } from '../../constants/ShareEvent.types';
import dataSources from '../../constants/dataSources';
import {
  IAR_DEFAULT_DISPLAY_CONFIG,
  SHARE_EXPIRY_SECONDS,
} from '../../constants/shareEventDefaults';
import {
  FIELD_DIRECT_RECIPIENTS_SHARE,
  FIELD_IAR_SHARE,
  type FieldSharingMethod,
} from '../../constants/shareMethods';
import { type MultiInputTag } from './ShareEventRecipients';

export interface IarSelectedMember {
  agency_id: number;
  member_id: number;
  name?: string;
  subscriber_name?: string;
}

export interface FieldFormValues {
  recipients: MultiInputTag[];
  sharingMethod: FieldSharingMethod;
  dataSources: Checkbox[];
  notes: string;
  iarSelectedMembers: IarSelectedMember[];
}

/**
 * The Field Share modal is a small wizard. Steps are identified by string keys
 * (not positional numbers) so the IaR-only `IAR_SELECT` step can sit between
 * `RECIPIENTS` and `DATA_SOURCES` without renumbering the rest. Order is defined
 * per sharing method by `getStepSequence`, not by the value of these keys.
 */
export const SHARE_STEP = {
  RECIPIENTS: 'recipients',
  IAR_SELECT: 'iar-select',
  DATA_SOURCES: 'data-sources',
  NOTES: 'notes',
  SHARE_LINK: 'share-link',
} as const;

export type StepId = (typeof SHARE_STEP)[keyof typeof SHARE_STEP];

export const getStepSequence = (method: FieldSharingMethod): StepId[] =>
  method === FIELD_IAR_SHARE
    ? [
        SHARE_STEP.RECIPIENTS,
        SHARE_STEP.IAR_SELECT,
        SHARE_STEP.DATA_SOURCES,
        SHARE_STEP.NOTES,
      ]
    : [
        SHARE_STEP.RECIPIENTS,
        SHARE_STEP.DATA_SOURCES,
        SHARE_STEP.NOTES,
        SHARE_STEP.SHARE_LINK,
      ];

export const getNextStep = (
  current: StepId,
  method: FieldSharingMethod,
): StepId | null => {
  const seq = getStepSequence(method);
  const idx = seq.indexOf(current);
  return idx >= 0 && idx < seq.length - 1 ? seq[idx + 1] : null;
};

export const getPrevStep = (
  current: StepId,
  method: FieldSharingMethod,
): StepId | null => {
  const seq = getStepSequence(method);
  const idx = seq.indexOf(current);
  return idx > 0 ? seq[idx - 1] : null;
};

/** Create the validation schema based on the current step and sharing method */
export const createValidationSchema = (
  currentStep: StepId,
  sharingMethod: FieldSharingMethod,
) => {
  // Data sources are only required once the wizard has reached the data-sources
  // step. Order varies by sharing method, so compare positions in the sequence.
  const sequence = getStepSequence(sharingMethod);
  const hasReachedDataSources =
    sequence.indexOf(currentStep) >= sequence.indexOf(SHARE_STEP.DATA_SOURCES);

  const baseValidation = {
    sharingMethod: Yup.string().required(),
    dataSources: Yup.array().test(
      'datasources-required',
      'Select at least one data source',
      function (value: Checkbox[] | undefined) {
        if (hasReachedDataSources) {
          return Boolean(
            value && value.some((source: Checkbox) => source.isChecked),
          );
        }
        return true;
      },
    ),
    sessions: Yup.array(),
    notes: Yup.string(),
  };

  // Different validation based on sharing method
  const recipientsValidation =
    sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE
      ? Yup.array()
          .min(1, 'At least one recipient is required')
          .test(
            'recipients-valid',
            'Invalid email or phone number format',
            function (value: MultiInputTag[] | undefined) {
              if (value && value.length > 0) {
                return value.every(
                  (recipient: MultiInputTag) => recipient.isValid,
                );
              }
              return true;
            },
          )
          .test(
            'recipients-limit',
            'Limit reached: Total recipients cannot exceed 10.',
            function (value: MultiInputTag[] | undefined) {
              return !(value && value.length > 10);
            },
          )
      : Yup.array();

  return Yup.object().shape({
    ...baseValidation,
    recipients: recipientsValidation,
  });
};

export const createInitialValues = (
  sharingMethod: FieldSharingMethod,
): FieldFormValues => ({
  recipients: [],
  sharingMethod,
  dataSources: dataSources.map(source => ({ ...source, isChecked: false })), // TODO: update to true if checked by default
  notes: '',
  iarSelectedMembers: [],
});

// Step validation logic
export const isStepValid = (
  step: StepId,
  values: FieldFormValues,
  errors: FormikErrors<FieldFormValues>,
  sharingMethod: FieldSharingMethod,
): boolean => {
  switch (step) {
    case SHARE_STEP.RECIPIENTS:
      // Check for Formik errors first
      if (errors.recipients || errors.sharingMethod) {
        return false;
      }

      // For direct recipients, also check that we have valid recipients
      if (sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE) {
        return (
          values.recipients.length > 0 &&
          values.recipients.every(recipient => recipient.isValid)
        );
      }

      // For share-link and IaR, no recipients needed on the first step
      return true;
    case SHARE_STEP.IAR_SELECT:
      return values.iarSelectedMembers.length > 0;
    case SHARE_STEP.DATA_SOURCES:
      // Check for Formik errors first
      if (errors.dataSources) {
        return false;
      }

      // Also check that at least one data source is selected
      return values.dataSources.some(source => source.isChecked);
    case SHARE_STEP.NOTES:
      return true; // Notes are optional
    case SHARE_STEP.SHARE_LINK:
      return true; // Share link display step
    default:
      return false;
  }
};

const hasCoordinates = (
  loc: unknown,
): loc is { latitude: number; longitude: number } => {
  const obj = loc as Record<string, unknown>;
  return obj?.latitude != null && obj?.longitude != null;
};

const buildCallContext = (selectedCall?: EmergencyCall) => {
  if (!selectedCall) return {};

  const location = selectedCall.core?.locations?.[0];
  const secondaryLocation = selectedCall.core?.secondaryLocations?.[0];
  const address = secondaryLocation?.address_single_line || undefined;

  return {
    ...(address ? { alert: { address } } : {}),
    ...(location?.caller_id ? { call: { ani: location.caller_id } } : {}),
    ...(hasCoordinates(location)
      ? {
          spatial_layer: {
            latitude: location.latitude,
            longitude: location.longitude,
          },
        }
      : {}),
    ...(selectedCall.additionalData
      ? { additional_data: selectedCall.additionalData }
      : {}),
  };
};

export const createSubmissionPayload = (
  values: FieldFormValues,
  sharingMethod: FieldSharingMethod,
  uri: string,
  organization: string,
  selectedCall?: EmergencyCall,
) => {
  const callContext = buildCallContext(selectedCall);
  const isIar = sharingMethod === FIELD_IAR_SHARE;
  const isDirectRecipients = sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE;

  // IaR uses the standard endpoint with an optional `members` array — the
  // backend creates a `link` session and fires push notifications. The map
  // entry is appended to display_config so responders always see the location.
  const displayConfig = [
    ...values.dataSources
      .filter(source => source.isChecked)
      .map(source => ({ name: source.id, meta: {} })),
    ...(isIar ? IAR_DEFAULT_DISPLAY_CONFIG : []),
  ];

  // Only direct-recipients shares carry recipient emails/phones; link and IaR
  // shares leave them empty. Build both lists in a single pass over the entries.
  const emails: string[] = [];
  const phoneNumbers: string[] = [];
  if (isDirectRecipients) {
    values.recipients.forEach(recipient => {
      if (!recipient.isValid) return;
      if (recipient.type === 'email') {
        emails.push(recipient.value);
      } else if (recipient.type === 'phone') {
        phoneNumbers.push(
          formatInternationalPhoneNumberE164(recipient.value) ||
            recipient.value,
        );
      }
    });
  }

  return {
    uri,
    organization,
    ...(isIar ? { expiry: SHARE_EXPIRY_SECONDS } : {}),
    recipients: {
      emails,
      phone_numbers: phoneNumbers,
      organization_contacts: [],
      contact_groups: [],
    },
    ...(isIar ? { members: values.iarSelectedMembers } : {}),
    display_config: displayConfig,
    additional_comments: values.notes ? [values.notes] : [],
    ...callContext,
  };
};
